import { computed, makeObservable } from 'mobx'

export enum TENURE_PERIOD_TYPES {
  years = 'years',
  months = 'months',
}

export enum TENURE_BALANCE_TYPES {
  fixed_balance = 'fixed_balance',
  time_worked_based = 'time_worked_based',
}

export interface ITenurePeriod {
  period_length: number
  period_type: TENURE_PERIOD_TYPES
  balance_type: TENURE_BALANCE_TYPES
  time_worked_based_hours_accrued_in_cents?: number
  time_worked_based_per_hours_worked_in_cents?: number
  adjustment_in_cents?: number
  max_cap_in_cents?: number
  _destroy?: boolean
  id?: number
}

class TenurePeriod {
  periodLength: number
  periodType: TENURE_PERIOD_TYPES
  balanceType: TENURE_BALANCE_TYPES
  timeWorkedBasedHoursAccruedInCents?: number
  timeWorkedBasedPerHoursWorkedInCents?: number
  adjustmentInCents?: number
  maxBalanceCapInCents?: number
  id?: number

  constructor({
    period_length,
    period_type,
    balance_type,
    time_worked_based_hours_accrued_in_cents,
    time_worked_based_per_hours_worked_in_cents,
    adjustment_in_cents,
    max_cap_in_cents,
    id,
  }: ITenurePeriod) {
    makeObservable(this, {
      isInYears: computed,
      periodLengthInMonths: computed,
      adjustment: computed,
      maxBalanceCap: computed,
      timeWorkedBasedHoursAccrued: computed,
      timeWorkedBasedPerHoursWorked: computed,
    })

    this.periodLength = period_length
    this.periodType = period_type
    this.balanceType = balance_type
    this.timeWorkedBasedHoursAccruedInCents = time_worked_based_hours_accrued_in_cents
    this.timeWorkedBasedPerHoursWorkedInCents = time_worked_based_per_hours_worked_in_cents
    this.adjustmentInCents = adjustment_in_cents
    this.maxBalanceCapInCents = max_cap_in_cents
    this.id = id
  }

  static TENURE_PERIOD_TYPE = TENURE_PERIOD_TYPES
  static TENURE_BALANCE_TYPE = TENURE_BALANCE_TYPES

  static sortByPeriodLength(periods: ITenurePeriod[]): ITenurePeriod[] {
    return periods.sort((a, b) => {
      return new TenurePeriod(a).periodLengthInMonths - new TenurePeriod(b).periodLengthInMonths
    })
  }

  get isInYears(): boolean {
    return this.periodType === TENURE_PERIOD_TYPES.years
  }

  get isFixedBalance(): boolean {
    return this.balanceType === TENURE_BALANCE_TYPES.fixed_balance
  }

  get periodLengthInMonths(): number {
    return this.isInYears ? this.periodLength * 12 : this.periodLength
  }

  get adjustment(): number | null {
    const value = this.adjustmentInCents

    if (!value) return null

    return value / 100
  }

  get maxBalanceCap(): number | null {
    const MaxBalanceCap = this.maxBalanceCapInCents

    if (!MaxBalanceCap) return null

    return MaxBalanceCap / 100
  }

  get isFixed(): boolean {
    return this.balanceType === TENURE_BALANCE_TYPES.fixed_balance
  }

  get isTimeWorkedBased(): boolean {
    return this.balanceType === TENURE_BALANCE_TYPES.time_worked_based
  }

  get timeWorkedBasedHoursAccrued(): number | null {
    const accrued = this.timeWorkedBasedHoursAccruedInCents

    if (!accrued) return null

    return accrued / 100
  }

  get timeWorkedBasedPerHoursWorked(): number | null {
    const perHoursWorked = this.timeWorkedBasedPerHoursWorkedInCents

    if (!perHoursWorked) return null

    return perHoursWorked / 100
  }
}

export default TenurePeriod
